import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiLayersOutline,
    mdiAccountGroup,
    mdiSchool,
    mdiPlus,
    mdiTrashCan,
    mdiLinkVariant,
} from '@mdi/js';
import { toast } from 'react-toastify';
import AppHeader from '../../components/AppHeader';
import BaseModal from '../../components/BaseModal';
import { classApi } from '../../api/classes';
import ConfirmationModal from '../../components/ConfirmationModal';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ShortCourseGroupPage.css';
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ShortCourseGroupPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [group, setGroup] = useState(null);
    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);

    // Add existing course modal
    const [showAddExistingModal, setShowAddExistingModal] = useState(false);
    const [allCourses, setAllCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const loadGroup = useCallback(async () => {
        const res = await classApi.getShortCourseGroup(id);
        if (res.ok) setGroup(res.data.shortCourseGroup);
    }, [id]);

    const loadCourses = useCallback(async () => {
        const res = await classApi.getShortCourses({ shortCourseGroupId: id });
        if (res.ok) setCourses(res.data.shortCourses || []);
    }, [id]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([loadGroup(), loadCourses()]).finally(() => setIsLoading(false));
    }, [loadGroup, loadCourses]);

    const openAddExistingModal = async () => {
        const res = await classApi.getShortCourses();
        if (res.ok) {
            const alreadyInGroup = new Set(courses.map(c => c.id));
            setAllCourses((res.data.shortCourses || []).filter(c => !alreadyInGroup.has(c.id)));
        }
        setSelectedCourseId('');
        setShowAddExistingModal(true);
    };

    const handleAddExisting = async () => {
        if (!selectedCourseId) { toast.error('Select a course'); return; }
        setIsAdding(true);
        try {
            const res = await classApi.updateShortCourse(selectedCourseId, { shortCourseGroupId: id });
            if (res.ok) {
                toast.success('Course added to group');
                setShowAddExistingModal(false);
                loadCourses();
            } else {
                toast.error(res.data?.message || 'Failed to add course');
            }
        } catch {
            toast.error('Failed to add course');
        } finally {
            setIsAdding(false);
        }
    };

    const handleDetach = (courseId, courseName) => {
        setConfirmModal({
            title: 'Remove from Group',
            message: `Remove "${courseName}" from this group? The course will still exist as a standalone course.`,
            variant: 'warning',
            onConfirm: async () => {
                setConfirmModal(null);
                setRemovingId(courseId);
                try {
                    const res = await classApi.updateShortCourse(courseId, { shortCourseGroupId: null });
                    if (res.ok) {
                        toast.success(`${courseName} removed from group`);
                        loadCourses();
                    } else {
                        toast.error(res.data?.message || 'Failed to remove course from group');
                    }
                } catch {
                    toast.error('Failed to remove course from group');
                } finally {
                    setRemovingId(null);
                }
            },
        });
    };

    const formatDate = iso =>
        iso
            ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
            : '—';

    const groupDateRange = group
        ? [formatDate(group.startDate), formatDate(group.endDate)].filter(d => d !== '—').join(' – ')
        : '';

    return (
        <div className="short-course-group-page dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                className="back-nav-btn"
                                onClick={() => navigate('/classes/terms')}>
                                ← Terms &amp; Courses
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiLayersOutline} size={1.1} color="#451f78" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                {isLoading ? 'Group' : (group?.name || 'Group')}
                            </h1>
                        </div>
                        {!isLoading && group && (
                            <p className="sub-portal-header-subtitle">
                                {[
                                    group.label,
                                    groupDateRange,
                                    group.location,
                                ].filter(Boolean).join(' · ')}
                            </p>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="content-card">
                            <p className="scg-loading">Loading...</p>
                        </div>
                    ) : (
                        <div className="content-card">
                            <div className="scg-courses-header">
                                <div className="scg-courses-title">
                                    <Icon path={mdiSchool} size={0.95} color="#451f78" />
                                    <h3>Courses ({courses.length})</h3>
                                </div>
                                <div className="scg-course-actions-bar">
                                    <button
                                        className="btn-outline-purple"
                                        onClick={openAddExistingModal}>
                                        <Icon path={mdiLinkVariant} size={0.85} />
                                        Add Existing
                                    </button>
                                    <button
                                        className="btn-purple"
                                        onClick={() => navigate('/classes/terms', { state: { openShortCourse: true, groupId: id } })}>
                                        <Icon path={mdiPlus} size={0.85} />
                                        New Course
                                    </button>
                                </div>
                            </div>

                            {courses.length === 0 ? (
                                <p className="scg-empty">
                                    No courses in this group yet. Add an existing short course or create a new one.
                                </p>
                            ) : (
                                <div className="scg-course-list">
                                    {courses.map(c => {
                                        const dayName = c.dayOfWeek != null ? DAY_NAMES[c.dayOfWeek] : '';
                                        return (
                                            <div key={c.id} className="scg-course-row">
                                                <div className="scg-course-info">
                                                    <div className="scg-course-name">
                                                        {c.name}
                                                        {c.style && (
                                                            <span className="scg-course-badge">
                                                                {c.style}{c.level ? ` · ${c.level}` : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="scg-course-meta">
                                                        {[
                                                            dayName,
                                                            c.startTime && c.endTime ? `${c.startTime}–${c.endTime}` : null,
                                                            c.startDate ? formatDate(c.startDate) : null,
                                                            c.sessionCount ? `${c.sessionCount} sessions` : null,
                                                        ].filter(Boolean).join(' · ')}
                                                    </div>
                                                </div>
                                                <div className="scg-course-actions">
                                                    <button
                                                        className="class-term-action-btn"
                                                        onClick={() => navigate(`/classes/short-courses/${c.id}`)}
                                                        title="View enrolments">
                                                        <Icon path={mdiAccountGroup} size={0.85} />
                                                    </button>
                                                    <button
                                                        className="class-term-action-btn danger"
                                                        onClick={() => handleDetach(c.id, c.name)}
                                                        disabled={removingId === c.id}
                                                        title="Remove from group">
                                                        <Icon path={mdiTrashCan} size={0.85} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <BaseModal
                isOpen={showAddExistingModal}
                onClose={() => setShowAddExistingModal(false)}
                title="Add Existing Course"
                icon={mdiLinkVariant}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setShowAddExistingModal(false)}>
                            Cancel
                        </button>
                        <button className="base-modal-save-btn" onClick={handleAddExisting} disabled={isAdding || !selectedCourseId}>
                            {isAdding ? 'Adding...' : 'Add to Group'}
                        </button>
                    </>
                }
            >
                {allCourses.length === 0 ? (
                    <p style={{ color: '#777', fontSize: '0.9rem' }}>
                        No other short courses available to add. All existing courses are already in this group, or none have been created yet.
                    </p>
                ) : (
                    <>
                        <label className="class-term-form-label">Select a course</label>
                        <select
                            className="class-term-form-input"
                            value={selectedCourseId}
                            onChange={e => setSelectedCourseId(e.target.value)}>
                            <option value="">— Choose a course —</option>
                            {allCourses.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name}{c.style ? ` · ${c.style}` : ''}{c.startDate ? ` (${new Date(c.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })})` : ''}
                                </option>
                            ))}
                        </select>
                    </>
                )}
            </BaseModal>

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default ShortCourseGroupPage;
